import { apiErrorMessage } from '@agos/api-client'
import {
  getGetLoyaltySettingsQueryKey,
  useGetLoyaltySettings,
  usePutLoyaltySettings,
} from '@agos/api-client/customers'
import {
  getGetSettingsSmsQueryKey,
  useGetSettingsSms,
  usePostSettingsSms,
  usePostSettingsSmsTest,
  usePostSmsSendManual,
} from '@agos/api-client/marketing'
import { T, formatCents, formatCount, formatPhone, tt, useAuth } from '@agos/shared'
import { ConfirmDialog, PageHeader, Spinner, TabGroup, cn, useToast, BUTTON_PRIMARY} from '@agos/ui'

import { SmsContactsPanel } from './SmsContactsPanel'
import { useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  Clock,
  ExternalLink,
  Eye,
  KeyRound,
  MessageSquare,
  Plus,
  Save,
  Send,
  Wifi,
  X,
  Zap,
} from 'lucide-react'
import { useState } from 'react'

import { mayWrite, readOnlyReason } from '../write-access'
import {
  POINTS_SMS_PLACEHOLDERS,
  SMS_PLACEHOLDERS,
  SMS_PRICE_CENTS,
  SMS_TABS,
  accentsWouldBeStripped,
  carriesToken,
  costLabel,
  costOf,
  countingDisagrees,
  formOf,
  nonGsmCharacters,
  pointsSmsPayload,
  previewOf,
  recipientSummary,
  recipientsOf,
  sendBlock,
  sendBlockMessage,
  senderAsSent,
  senderWouldChange,
  settingsPayload,
  tabLabel,
  typedPreviewOf,
  unknownPlaceholders,
  type SmsForm,
  type SmsTab,
} from './sms-settings'

/**
 * `/settings-sms` — five tabs over two endpoints, and every button on it spends money.
 *
 * **FB-141.** The preview showed a message the customer never receives: `SmsGateway` strips diacritics and
 * sends `unicode: false`, because one `ľ` turns a 160-character GSM-7 text into a 70-character-per-segment
 * Unicode one and triples the bill. Every template here is written with accents. The sender-name field also
 * allowed 13 characters where BulkGate cuts at 11 — the stored *Pizza Majstro* arrives as *Pizza Majst* —
 * and `charCount` divided the **template** by 160 where a concatenated text carries 153 per part.
 *
 * **FB-142.** *Načítať zákazníkov* put all **2 463** customer phone numbers in the box — 2 341 distinct once
 * normalised the way the gateway normalises them — and one click sent to every one of them, with no cost
 * stated. `sendManual` caps the list at 500 and rejects the whole request, so legacy's only feedback for a
 * 2 463-number send was *Chyba odosielania* with nothing sent and no reason.
 *
 * **FB-143.** `GET /settings/sms` answers the unmasked 50-character BulkGate token. Legacy rendered it in a
 * password input with a reveal button and POSTed it back on every save. Here the field is empty, says a
 * token is stored, and is only sent when a new one is typed.
 *
 * Ported from legacy `pages/settings-sms.tsx` (781 lines).
 */

export function SettingsSmsPage() {
  const sms = useGetSettingsSms()
  const loyalty = useGetLoyaltySettings()

  if (sms.isLoading || loyalty.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return <SmsEditor settings={{ ...sms.data }} loyalty={{ ...loyalty.data }} />
}

const FIELD =
  'px-3 py-2 rounded-lg bg-background border border-border text-sm outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60'
const AREA = `${FIELD} w-72 min-h-[80px] resize-y font-mono`

function SmsEditor({
  settings,
  loyalty,
}: {
  settings: Record<string, unknown> | undefined
  loyalty: Record<string, unknown> | undefined
}) {
  const { toast } = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const [tab, setTab] = useState<SmsTab>('delivery')
  const [form, setForm] = useState<SmsForm>(() => formOf(settings))
  const [newToken, setNewToken] = useState<string | null>(null)
  const [showToken, setShowToken] = useState(false)

  const [pointsEnabled, setPointsEnabled] = useState(loyalty?.pointsSmsEnabled === true)
  const [pointsTemplate, setPointsTemplate] = useState(
    typeof loyalty?.pointsSmsTemplate === 'string' ? loyalty.pointsSmsTemplate : '',
  )

  const [testPhone, setTestPhone] = useState('')
  const [confirmTest, setConfirmTest] = useState(false)

  const [manualEntry, setManualEntry] = useState('')
  const [manualPhones, setManualPhones] = useState<string[]>([])
  const [manualMessage, setManualMessage] = useState('')
  const [confirmSend, setConfirmSend] = useState(false)

  const writable = mayWrite('sms', user?.systemRole)
  const readOnly = readOnlyReason('sms', user?.systemRole)
  const hasToken = newToken !== null ? newToken.trim() !== '' : carriesToken(settings)
  const credentialsOk = form.bulkgateAppId.trim() !== '' && hasToken

  const recipients = recipientsOf(manualPhones)
  const manualCost = costOf(manualMessage)
  const block = sendBlock(recipients, manualMessage, credentialsOk)

  /**
   * §9.385 - **the title is the screen's, the description is the server's.**
   *
   * The first version of that sweep wrapped the **title** here, because `failed` takes one argument
   * and the argument is the title: the toast then read *nope* with no heading at all, and the e2e
   * that asserts *Uloženie zlyhalo* caught it. A helper whose one parameter is the wrong half is
   * exactly how a mechanical sweep goes wrong.
   */
  const failed = (title: string, caught?: unknown) =>
    toast({
      variant: 'destructive',
      title,
      description: apiErrorMessage(caught, tt('Na serveri sa nič nezmenilo.')),
    })

  const save = usePostSettingsSms({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetSettingsSmsQueryKey() })
        setNewToken(null)
        toast({ title: tt('Nastavenia SMS uložené') })
      },
      onError: (caught) => failed(tt('Uloženie zlyhalo'), caught),
    },
  })

  const savePoints = usePutLoyaltySettings({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetLoyaltySettingsQueryKey() })
        toast({ title: tt('Vernostná SMS šablóna uložená') })
      },
      onError: (caught) => failed(tt('Uloženie zlyhalo'), caught),
    },
  })

  const sendTest = usePostSettingsSmsTest({
    mutation: {
      onSuccess: () => toast({ title: tt('Testovacia SMS odoslaná') }),
      onError: (error: unknown) =>
        toast({
          variant: 'destructive',
          title: tt('Testovacia SMS neodišla'),
          description: apiErrorMessage(error, tt('BulkGate ju odmietol alebo je nedostupný.')),
        }),
    },
  })

  const sendManual = usePostSmsSendManual({
    mutation: {
      onSuccess: () => {
        setManualPhones([])
        setManualEntry('')
        setManualMessage('')
        toast({ title: tt('SMS odoslané') })
      },
      onError: (caught) =>
        toast({
          variant: 'destructive',
          title: tt('Odoslanie zlyhalo'),
          description: apiErrorMessage(
                         caught,
                         tt('Skontrolujte, či neodišla časť správ, predtým než to skúsite znova.'),
                       ),
        }),
    },
  })

  const set = <K extends keyof SmsForm>(key: K, value: SmsForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const addPhone = () => {
    const entry = manualEntry.trim()
    if (entry === '') return
    setManualPhones((current) => (current.includes(entry) ? current : [...current, entry]))
    setManualEntry('')
  }

  return (
    <div className="space-y-5">
      <PageHeader
        title={tt('Nastavenia SMS')}
        description={tt('Kedy zákazníkovi odíde SMS, čo v nej je a čo to stojí')}
        icon={<MessageSquare className="w-5 h-5" />}
      />

      {readOnly !== null && (
        <p className="text-[11px] text-amber-500" data-read-only>
          {readOnly}
        </p>
      )}

      {!credentialsOk && (
        <div
          className="rounded-2xl border border-amber-500/40 bg-amber-500/5 px-4 py-3 flex items-start gap-3"
          data-no-credentials
        >
          <AlertTriangle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-500">
              <T>BulkGate nie je prepojený</T>
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              <T>Bez Application ID a tokenu neodíde žiadna SMS - ani doručovacia, ani vernostná.</T>
            </p>
          </div>
        </div>
      )}

      <TabGroup
        tabs={SMS_TABS.map((entry) => ({ id: entry, label: tabLabel(entry) }))}
        active={tab}
        onChange={(id) => setTab(id as SmsTab)}
      />

      {tab === 'delivery' && (
        <Section title={tt('SMS doručenie objednávky')} icon={<MessageSquare className="w-4 h-4 text-primary" />}>
          <Toggle
            label={tt('Zapnúť SMS doručenie')}
            desc={tt('Zákazník dostane SMS, keď šofér potvrdí, že ide na adresu.')}
            value={form.deliveryEnabled}
            disabled={!writable}
            onToggle={() => set('deliveryEnabled', !form.deliveryEnabled)}
            testId="deliveryEnabled"
          />
          <Row
            label={tt('Meno odosielateľa')}
            desc={tt('Zobrazí sa zákazníkovi namiesto čísla. BulkGate ho skráti na 11 znakov bez diakritiky.')}
          >
            <input
              aria-label={tt('Meno odosielateľa')}
              className={cn(FIELD, 'w-56')}
              disabled={!writable}
              value={form.deliverySenderName}
              onChange={(event) => set('deliverySenderName', event.target.value)}
            />
            {senderWouldChange(form.deliverySenderName) && (
              <p className="text-[11px] text-amber-500" data-sender-changed>
                <T>Zákazník uvidí</T> <code>{senderAsSent(form.deliverySenderName)}</code>
              </p>
            )}
          </Row>
          <TemplateRow
            label={tt('Šablóna SMS správy')}
            template={form.deliveryTemplate}
            placeholders={SMS_PLACEHOLDERS}
            disabled={!writable}
            testId="delivery"
            onChange={(value) => set('deliveryTemplate', value)}
          />
          {/* §9.322, the owner's Q11 - *"make it also for all SMS"*. This text is sent by
              `OrderMessages.driverAtAddress` on every delivery where the push did not reach a
              device, and **neither AGOS nor legacy had a box for it**: the column is in
              `sms_settings`, the sender reads it, and the wording was only ever changeable with
              SQL. Beside the delivery template because it is the same moment in the order's life,
              one step later. */}
          <TemplateRow
            label={tt('Šablóna SMS - kuriér je na adrese')}
            template={form.atAddressTemplate}
            placeholders={SMS_PLACEHOLDERS}
            disabled={!writable}
            testId="atAddress"
            onChange={(value) => set('atAddressTemplate', value)}
          />
          <Toggle
            label={tt('Poslať SMS, keď je kuriér na adrese')}
            desc={tt(
              'Odíde len vtedy, keď push oznámenie nedorazí na žiadne zariadenie zákazníka.',
            )}
            value={form.atAddressEnabled}
            disabled={!writable}
            onToggle={() => set('atAddressEnabled', !form.atAddressEnabled)}
            testId="atAddressEnabled"
          />
        </Section>
      )}

      {tab === 'delay' && (
        <>
          <Section title={tt('SMS meškanie objednávky')} icon={<Clock className="w-4 h-4 text-primary" />}>
            <Toggle
              label={tt('Zapnúť SMS meškanie')}
              desc={tt('Upozorní zákazníka, ak objednávka čaká bez šoféra dlhšie ako prah.')}
              value={form.delayEnabled}
              disabled={!writable}
              onToggle={() => set('delayEnabled', !form.delayEnabled)}
              testId="delayEnabled"
            />
            <Row
              label={tt('Prah meškania (minúty)')}
              desc={tt('Objednávka bez šoféra staršia ako toľko minút sa považuje za meškajúcu.')}
            >
              <input
                type="number"
                min={1}
                max={480}
                aria-label={tt('Prah meškania (minúty)')}
                className={cn(FIELD, 'w-56')}
                disabled={!writable}
                value={form.delayThresholdMinutes}
                onChange={(event) => set('delayThresholdMinutes', Number(event.target.value))}
              />
            </Row>
            <TemplateRow
              label={tt('Šablóna SMS správy')}
              template={form.delayTemplate}
              placeholders={SMS_PLACEHOLDERS}
              disabled={!writable}
              testId="delay"
              onChange={(value) => set('delayTemplate', value)}
            />
            <Row label={tt('Režim odosielania')} desc={tt('Kto rozhodne, že SMS odíde.')}>
              <div className="flex gap-2" data-delay-mode={form.delayMode}>
                {(['auto', 'manual'] as const).map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    data-mode={mode}
                    disabled={!writable}
                    onClick={() => set('delayMode', mode)}
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold border disabled:opacity-50',
                      form.delayMode === mode
                        ? 'bg-primary/15 text-primary border-primary/30'
                        : 'border-border text-muted-foreground',
                    )}
                  >
                    {mode === 'auto' ? <Zap className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
                    {mode === 'auto' ? <T>Automaticky</T> : <T>Manuálne</T>}
                  </button>
                ))}
              </div>
            </Row>
          </Section>
          <p className="text-xs text-muted-foreground" data-delay-note>
            {form.delayMode === 'auto' ? (
              <T>
                Úloha kontroluje meškajúce objednávky každú minútu a SMS odošle sama - každá stojí peniaze a
                nikto ju nepotvrdzuje.
              </T>
            ) : (
              <T>
                Meškajúce objednávky sa v dispečingu označia a SMS odošle dispečer tlačidlom. Nič sa neodošle
                samo.
              </T>
            )}
          </p>
        </>
      )}

      {tab === 'loyalty' && (
        <Section
          title={tt('SMS notifikácia o získaných bodoch')}
          icon={<MessageSquare className="w-4 h-4 text-primary" />}
        >
          <Toggle
            label={tt('Zapnúť SMS notifikáciu')}
            desc={tt('Zákazník dostane SMS, keď mu pripíšeme body.')}
            value={pointsEnabled}
            disabled={!writable}
            onToggle={() => setPointsEnabled(!pointsEnabled)}
            testId="pointsSmsEnabled"
          />
          <TemplateRow
            label={tt('Šablóna SMS')}
            template={pointsTemplate}
            placeholders={POINTS_SMS_PLACEHOLDERS}
            kind="points"
            disabled={!writable}
            testId="points"
            onChange={setPointsTemplate}
          />
          <div className="px-5 py-4 flex justify-end">
            <button
              type="button"
              data-save-points
              disabled={!writable || savePoints.isPending}
              onClick={() =>
                savePoints.mutate({ data: { ...pointsSmsPayload(pointsEnabled, pointsTemplate) } })
              }
              className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              {savePoints.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť šablónu</T>
            </button>
          </div>
        </Section>
      )}

      {tab === 'portal' && (
        <>
          <Section title={tt('Prepojenie na BulkGate')} icon={<Wifi className="w-4 h-4 text-primary" />}>
            <Row label="Application ID" desc={tt('V portáli BulkGate pod Modules & APIs → HTTP Simple API.')}>
              <input
                aria-label="Application ID"
                className={cn(FIELD, 'w-72')}
                autoComplete="off"
                disabled={!writable}
                value={form.bulkgateAppId}
                onChange={(event) => set('bulkgateAppId', event.target.value)}
              />
            </Row>
            {/* FB-143. The stored token is never fetched into this field. */}
            <Row
              label="Application Token"
              desc={tt('Tajný token. Vypĺňa sa len keď ho chcete zmeniť - uložený token odtiaľto neodíde.')}
            >
              <div className="flex gap-2 items-center">
                <input
                  aria-label="Application Token"
                  type={showToken ? 'text' : 'password'}
                  className={cn(FIELD, 'w-72')}
                  autoComplete="off"
                  disabled={!writable}
                  placeholder={
                    carriesToken(settings) ? tt('token je uložený - nechajte prázdne') : tt('vložte token')
                  }
                  value={newToken ?? ''}
                  onChange={(event) => setNewToken(event.target.value)}
                />
                <button
                  type="button"
                  aria-label={tt('Zobraziť token')}
                  onClick={() => setShowToken(!showToken)}
                  className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground"
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
              </div>
              <p
                className="text-[11px] text-muted-foreground flex items-center gap-1"
                data-token={carriesToken(settings) ? 'stored' : 'absent'}
              >
                <KeyRound className="w-3 h-3 shrink-0" />
                {carriesToken(settings) ? (
                  <T>Token je uložený. Táto obrazovka ho nezobrazuje ani neposiela naspäť.</T>
                ) : (
                  <T>Token nie je uložený, takže neodíde žiadna SMS.</T>
                )}
              </p>
            </Row>
            <div className="px-5 py-4">
              <a
                href="https://portal.bulkgate.com/sign/up"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-primary underline"
              >
                <ExternalLink className="w-3 h-3" /> <T>Otvoriť portál BulkGate</T>
              </a>
            </div>
          </Section>

          <Section title={tt('Test spojenia')} icon={<Send className="w-4 h-4 text-primary" />}>
            <Row
              label={tt('Odoslať testovaciu SMS')}
              desc={tt('Odíde skutočná SMS a stojí peniaze. Najprv uložte Application ID a token.')}
            >
              <div className="flex gap-2">
                <input
                  aria-label={tt('Číslo pre test')}
                  className={cn(FIELD, 'w-56')}
                  disabled={!writable}
                  placeholder="+421 900 000 000"
                  value={testPhone}
                  onChange={(event) => setTestPhone(event.target.value)}
                />
                <button
                  type="button"
                  data-send-test
                  disabled={
                    !writable ||
                    sendTest.isPending ||
                    !credentialsOk ||
                    recipientsOf([testPhone]).people === 0
                  }
                  onClick={() => setConfirmTest(true)}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-border bg-muted text-sm font-medium disabled:opacity-50"
                >
                  {sendTest.isPending ? <Spinner /> : <Send className="w-4 h-4" />} <T>Odoslať test</T>
                </button>
              </div>
            </Row>
          </Section>
        </>
      )}

      {tab === 'contacts' && <SmsContactsPanel readOnly={readOnly} />}

      {tab === 'manual' && (
        <Section title={tt('Manuálna SMS')} icon={<Send className="w-4 h-4 text-primary" />}>
          <div className="px-5 py-4 space-y-4">
            <div className="flex gap-2">
              <input
                aria-label={tt('Telefónne číslo')}
                className={cn(FIELD, 'flex-1')}
                disabled={!writable}
                placeholder="+421 900 000 000"
                // Not `PhoneInput`: this one adds a number on Enter, which the shared field does not
                // model. The keyboard hints are the part worth sharing, and they are here.
                inputMode="tel"
                autoComplete="tel"
                value={manualEntry}
                onChange={(event) => setManualEntry(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    addPhone()
                  }
                }}
              />
              <button
                type="button"
                data-add-phone
                disabled={!writable || manualEntry.trim() === ''}
                onClick={addPhone}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-muted text-sm font-bold disabled:opacity-40"
              >
                <Plus className="w-4 h-4" /> <T>Pridať</T>
              </button>
            </div>

            {/* FB-142. Legacy loaded 2 463 numbers into this box behind a button called *load customers*. */}
            <p className="text-[11px] text-muted-foreground" data-no-bulk-load>
              <T>
                Hromadné rozposlanie všetkým zákazníkom sa robí v Marketing → Zákazníci, kde je segment,
                počet a cena vidno pred odoslaním. Tu sa píše na čísla, ktoré zadáte.
              </T>
            </p>

            {manualPhones.length > 0 && (
              <div className="flex flex-wrap gap-2" data-recipients={recipients.people}>
                {manualPhones.map((phone) => (
                  <span
                    key={phone}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-primary/10 border border-primary/20 rounded-lg text-xs font-medium text-primary"
                  >
                    {formatPhone(phone) || phone}
                    <button
                      type="button"
                      aria-label={`${tt('Odobrať')} ${phone}`}
                      onClick={() => setManualPhones((current) => current.filter((one) => one !== phone))}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {recipients.duplicates > 0 && (
              <p className="text-[11px] text-amber-500" data-duplicates>
                {formatCount(recipients.duplicates)}{' '}
                <T>
                  z čísel je ten istý človek napísaný inak - dostal by tú istú správu dvakrát a zaplatili by
                  ste ju dvakrát.
                </T>
              </p>
            )}

            {recipients.unusable.length > 0 && (
              <p className="text-[11px] text-red-500" data-unusable>
                {formatCount(recipients.unusable.length)} <T>zo zadaných hodnôt nie je telefónne číslo.</T>
              </p>
            )}

            <div className="space-y-1">
              <textarea
                aria-label={tt('Text správy')}
                rows={4}
                className={cn(AREA, 'w-full')}
                disabled={!writable}
                placeholder={tt('Napíšte text SMS správy…')}
                value={manualMessage}
                onChange={(event) => setManualMessage(event.target.value)}
              />
              <p className="text-[11px] text-muted-foreground" data-manual-cost>
                {costLabel(manualCost)}
              </p>
              {accentsWouldBeStripped(manualMessage) && (
                <p className="text-[11px] text-amber-500" data-manual-stripped>
                  <T>Diakritika sa pred odoslaním odstráni. Zákazník dostane</T>:{' '}
                  <span className="font-mono">{previewOf(manualMessage)}</span>
                </p>
              )}
            </div>

            {recipients.people > 0 && manualMessage.trim() !== '' && (
              <p className="text-sm font-semibold" data-manual-summary>
                {recipientSummary(recipients, manualCost)}
              </p>
            )}

            {block !== null && (
              <p className="text-[11px] text-amber-500" data-send-block={block}>
                {sendBlockMessage(block, recipients)}
              </p>
            )}

            <button
              type="button"
              data-send-manual
              disabled={!writable || sendManual.isPending || block !== null}
              onClick={() => setConfirmSend(true)}
              className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
            >
              {sendManual.isPending ? <Spinner /> : <Send className="w-4 h-4" />} <T>Odoslať SMS</T>
            </button>
          </div>
        </Section>
      )}

      {tab !== 'loyalty' && tab !== 'manual' && (
        <div className="flex items-center gap-3">
          <button
            type="button"
            data-save
            disabled={!writable || save.isPending}
            onClick={() => save.mutate({ data: { ...settingsPayload(form, newToken) } })}
            className={cn('flex items-center gap-2 px-5 py-2.5 rounded-xl', BUTTON_PRIMARY, 'text-sm')}
          >
            {save.isPending ? <Spinner /> : <Save className="w-4 h-4" />} <T>Uložiť nastavenia</T>
          </button>
        </div>
      )}

      <ConfirmDialog
        open={confirmTest}
        onClose={() => setConfirmTest(false)}
        title={tt('Odoslať skutočnú testovaciu SMS?')}
        description={`${tt('Odíde na')} ${formatPhone(testPhone) || testPhone}. ${tt('Stojí')} ${formatCents(Math.round(SMS_PRICE_CENTS))}.`}
        confirmLabel={tt('Odoslať')}
        icon={<Send className="w-5 h-5" />}
        loading={sendTest.isPending}
        onConfirm={() => {
          setConfirmTest(false)
          sendTest.mutate({ data: { phone: testPhone.trim() } })
        }}
      />

      <ConfirmDialog
        open={confirmSend}
        onClose={() => setConfirmSend(false)}
        variant="danger"
        title={tt('Odoslať SMS?')}
        description={`${recipientSummary(recipients, manualCost)}. ${tt('Odosielanie sa nedá vrátiť.')}`}
        confirmLabel={tt('Odoslať')}
        icon={<Send className="w-5 h-5" />}
        loading={sendManual.isPending}
        onConfirm={() => {
          setConfirmSend(false)
          sendManual.mutate({ data: { phones: manualPhones, message: manualMessage.trim() } })
        }}
      />
    </div>
  )
}

function TemplateRow({
  label,
  template,
  placeholders,
  kind = 'order',
  disabled,
  testId,
  onChange,
}: {
  label: string
  template: string
  placeholders: readonly string[]
  kind?: 'order' | 'points'
  disabled: boolean
  testId: string
  onChange: (value: string) => void
}) {
  const cost = costOf(template, kind)
  const unknown = unknownPlaceholders(template, placeholders)
  const outside = nonGsmCharacters(template)
  return (
    <Row
      label={label}
      desc={`${tt('Premenné')}: ${placeholders.map((name) => `{${name}}`).join(', ')}`}
    >
      <textarea
        aria-label={`${label} - ${testId}`}
        rows={4}
        className={AREA}
        disabled={disabled}
        value={template}
        onChange={(event) => onChange(event.target.value)}
      />
      <p className="text-[11px] text-muted-foreground" data-cost={testId}>
        {costLabel(cost)}
      </p>
      {countingDisagrees(cost) && (
        <p className="text-[11px] text-amber-500" data-cost-disagrees={testId}>
          {/* Legacy divided the template by 160; a concatenated text carries 153 per part. */}
          <T>Dlhšia správa sa účtuje po 153 znakoch na časť, nie po 160.</T>
        </p>
      )}
      {/* FB-141. The two previews, so the difference is the thing you see. */}
      <div className="w-72 space-y-1">
        <p className="text-[11px] text-muted-foreground">
          <T>Zákazník dostane</T>:
        </p>
        <p
          className="px-3 py-2 rounded-lg bg-muted/30 border border-border/50 text-sm leading-relaxed"
          data-preview-sent={testId}
        >
          {previewOf(template, kind)}
        </p>
        {accentsWouldBeStripped(template) && (
          <p className="text-[11px] text-amber-500" data-preview-stripped={testId}>
            <T>Napísané je</T>{' '}
            <span className="font-mono">{typedPreviewOf(template, kind)}</span> -{' '}
            <T>diakritika sa odstráni, inak by SMS stála trojnásobok.</T>
          </p>
        )}
      </div>
      {unknown.length > 0 && (
        <p className="text-[11px] text-red-500" data-unknown-placeholder={testId}>
          {unknown.map((name) => `{${name}}`).join(', ')} -{' '}
          <T>takú premennú systém nepozná a odíde zákazníkovi tak, ako je napísaná.</T>
        </p>
      )}
      {outside.length > 0 && (
        <p className="text-[11px] text-amber-500" data-non-gsm={testId}>
          {outside.join(' ')} - <T>tieto znaky SMS neprenesie a BulkGate ich zmení alebo zahodí.</T>
        </p>
      )}
    </Row>
  )
}

function Section({
  title,
  icon,
  children,
}: {
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center gap-2">
        {icon}
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">{title}</h2>
      </div>
      <div className="divide-y divide-border">{children}</div>
    </div>
  )
}

function Row({
  label,
  desc,
  children,
}: {
  label: string
  desc: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 gap-4">
      <div className="flex-1 pt-1 min-w-0">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <div className="shrink-0 space-y-1.5">{children}</div>
    </div>
  )
}

function Toggle({
  label,
  desc,
  value,
  disabled,
  onToggle,
  testId,
}: {
  label: string
  desc: string
  value: boolean
  disabled: boolean
  onToggle: () => void
  testId: string
}) {
  return (
    <div className="flex items-start justify-between px-5 py-4 gap-4" data-toggle={testId}>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        aria-label={label}
        disabled={disabled}
        data-toggle-state={value ? 'on' : 'off'}
        onClick={onToggle}
        className={cn(
          'w-11 h-6 rounded-full relative shrink-0 transition-colors disabled:opacity-50',
          value ? 'bg-primary' : 'bg-muted',
        )}
      >
        <span
          className={cn(
            'absolute top-0.5 w-5 h-5 rounded-full bg-background transition-all',
            value ? 'left-[22px]' : 'left-0.5',
          )}
        />
      </button>
    </div>
  )
}
